import { React, useState, useEffect, useMemo } from "react"; // Import useMemo
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { CircularProgress, TextField } from "@mui/material"; // Import TextField for search
import WarningIcon from '@mui/icons-material/Warning';
import { red } from '@mui/material/colors';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import IconButton from "@mui/material/IconButton";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from "sweetalert2";
import ServiceTypeAddModel from "./ServiceTypeAddModel";
import ServiceTypeEditModel from "./ServiceTypeEditModel";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";
import { useNavigate } from "react-router-dom";
import LogoutButton from "layouts/logout";
import { ServerBaseUrlForStaticImages } from "constants";

export default function ServiceType() {
    // State for pagination and search
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredServiceTypes, setFilteredServiceTypes] = useState([]);

    const [openAddModal, setOpenAddModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const navigate = useNavigate();
    const [editInput, setEditInput] = useState({});
    const [loading, setLoading] = useState(true);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [deleteServiceTypeId, setDeleteServiceTypeId] = useState(null);

    useEffect(() => {
        disableBackNavigation();
        fetchServiceTypes();
    }, []);

    // Effect for filtering based on search query
    useEffect(() => {
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            const newFilteredTypes = serviceTypes.filter(serviceType => {
                return serviceType.service_type_name.toLowerCase().includes(lowerCaseQuery);
            });
            setFilteredServiceTypes(newFilteredTypes);
        } else {
            setFilteredServiceTypes(serviceTypes);
        }
        setPage(0); // Reset page to 0 when search query changes
    }, [searchQuery, serviceTypes]);

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
        fetchServiceTypes();
    };

    const handleClose = () => {
        setEditInput({});
        setOpenAddModal(false);
        setOpenEditModal(false);
        setOpenDeleteModal(false);
        fetchServiceTypes();
    };

    // Handlers for pagination changes
    const handleRowsPerPageChange = (event, newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0); // Reset to the first page when rows per page changes
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    // Data for the current page
    const paginatedTableData = useMemo(() => {
        return filteredServiceTypes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [filteredServiceTypes, page, rowsPerPage]);


    const columns = useMemo(() => [
        {
            Header: "S.No",
            accessor: "serial",
            Cell: ({ row }) => (page * rowsPerPage) + row.index + 1, // Calculate serial number dynamically based on current page
            disableSortBy: true,
            align: "left",
        },
        {
            Header: "Service Type",
            accessor: "service_type_name",
            align: "left",
        },
        {
            Header: "Image",
            accessor: "service_type_image",
            Cell: ({ value }) => {
                const imageUrl = value ? `${ServerBaseUrlForStaticImages}/service_management/service_types/service_types/image/${value}` : null;

                return (
                    <img
                        src={imageUrl || "https://via.placeholder.com/60x40?text=No+Image"}
                        alt="service"
                        style={{
                            width: "60px",
                            height: "40px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #ccc"
                        }}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/60x40?text=No+Image";
                        }}
                    />
                );
            },
            align: "center",
        },

        {
            Header: "Options",
            accessor: "options",
            Cell: ({ row }) => (
                <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
                    <IconButton
                        color="success"
                        onClick={() => {
                            setOpenEditModal(true);
                            setEditInput(row.original);
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        color="error"
                        onClick={() => {
                            setDeleteServiceTypeId(row.original.id);
                            setOpenDeleteModal(true);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </div>
            ),
            align: "center",
        },
    ], [page, rowsPerPage]); // Add page and rowsPerPage to useMemo dependencies


    const fetchServiceTypes = async () => {
        setLoading(true);
        try {
            const response = await api.get("/service_management/service_types/with_images");
            setServiceTypes(response.data.service_types);
            setFilteredServiceTypes(response.data.service_types); // Initialize filtered data
        } catch (error) {
            console.error("Error fetching service types:", error);
            Swal.fire("Error", "Failed to load service types. Please try again later.", "error");
        } finally {
            setLoading(false);
        }
    };

    const deleteServiceType = async (event) => {
        event.preventDefault();
        try {
            handleClose(); // Close the modal first
            const response = await api.delete(`/service_management/service_types/${deleteServiceTypeId}`);
            if (response.data.status === 1) {
                Swal.fire({
                    title: 'Service type deleted successfully!',
                    icon: 'success',
                    timer: 1500
                });
                fetchServiceTypes();
            } else {
                Swal.fire({
                    title: 'Error',
                    text: response.data.message,
                    icon: 'error',
                });
            }
        } catch (error) {
            console.error("Error deleting service type:", error);
            Swal.fire({
                title: 'Error',
                text: 'An unexpected error occurred',
                icon: 'error',
            });
        }
    };

    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton onLogout={() => navigate("/login")} />
            </MDBox>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                                <MDTypography variant="h6" color="white">Service Type</MDTypography>
                            </MDBox>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={2} pl={3} pr={3}>
                                <TextField
                                    label="Search service types..."
                                    variant="outlined"
                                    size="small"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    sx={{ width: '300px' }}
                                />
                                <MDButton   color="success" onClick={() => setOpenAddModal(true)}>Add Service Type</MDButton>
                            </MDBox>
                            <MDBox p={3} display="flex" justifyContent="center" alignItems="center" minHeight="50svh">
                                {loading ? (
                                    <CircularProgress color="primary" />
                                ) : (
                                    <MDBox width="100%" display="flex" justifyContent="flex-start">
                                        <DataTable
                                            table={{ columns, rows: paginatedTableData }}
                                            isSorted={true}
                                            entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                                            showTotalEntries={true}
                                            canSearch={false}
                                            noEndBorder
                                            pagination={{
                                                variant: "contained",
                                                color: "dark",
                                                page: page,
                                                onPageChange: handlePageChange,
                                                rowsPerPage: rowsPerPage,
                                                onRowsPerPageChange: handleRowsPerPageChange,
                                                count: filteredServiceTypes.length,
                                            }}
                                        />
                                    </MDBox>
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* Add */}
            <ServiceTypeAddModel open={openAddModal} handleClose={handleCloseAddModal} />
            {/* Edit */}
            <ServiceTypeEditModel open={openEditModal} handleClose={handleClose} editInput={editInput} />
            {/* Delete */}
            <Dialog open={openDeleteModal} onClose={handleClose}>
                <MDBox component="form" onSubmit={deleteServiceType}>
                    <DialogTitle>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <WarningIcon style={{ color: red[700], fontSize: 40 }} />
                            </Grid>
                            <Grid item>
                                <MDTypography variant="h6" component="span">
                                    Confirm Delete
                                </MDTypography>
                            </Grid>
                        </Grid>
                    </DialogTitle>

                    <DialogContent>
                        <MDBox p={2} style={{ backgroundColor: red[50], borderLeft: `5px solid ${red[700]}` }}>
                            <MDTypography variant="body1">
                                Are you sure you want to delete this service type? This action cannot be undone.
                            </MDTypography>
                        </MDBox>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={handleClose} style={{ color: 'gray' }}>Cancel</MDButton>
                        <MDButton type="submit" color="primary">Delete</MDButton>
                    </DialogActions>
                </MDBox>
            </Dialog>
        </DashboardLayout>
    )
}